import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { loginUser } from '../data/getAxiosRequests';
import { useLocation, useNavigate } from 'react-router-dom';
import IconButton from '../components/UI/IconButton';
import { SVGs } from '../constants/Svgs';
import CustomButtonBO from '../components/UI/CustomButtonBO';
import { GlobalContext } from '../context/GlobalStateProvider';
import { TailSpin } from 'react-loader-spinner';

const LoginScreenRouteSetter = () => {
    const globalContext = useContext(GlobalContext);
    const { setUser } = globalContext;

    useEffect(() => {
        document.title = "Login ouverture"
        if (globalContext.user) {
            navigate('/homepagebo', { replace: true });
        }
    }, [globalContext.user])

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const uid = queryParams.get('uid');
    const [passwordConfirmationShown, setPasswordConfirmationShown] = useState(false);
    const navigate = useNavigate()

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const togglePasswordConfirmationVisibility = () => {
        setPasswordConfirmationShown(!passwordConfirmationShown);
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Adresse email invalide')
                .required("L'email est requis"),
            password: Yup.string()
                .required('Le mot de passe est requis')
        }),
        onSubmit: (values) => {
            setErrorMsg(null);
            loginUser(
                values.email,
                values.password,
                (uuid) => {
                    navigate(`/homepagebo`, { state: { uuid: uuid } });
                    setUser(uuid)
                    localStorage.setItem('user', JSON.stringify(uuid));
                },
                (error) => {
                    setErrorMsg(error);
                    formik.setSubmitting(false)
                }
            );
        },
    });

    const hasEmailError = formik.touched.email && formik.errors.email;
    const hasPasswordError = formik.touched.password && formik.errors.password;
    const canSubmit = formik.isValid && formik.dirty && !formik.isSubmitting;

    return (
        <div className="flex flex-col items-center justify-center min-h-screen w-full bg-backoffice-100 py-8 px-4 gap-10">
            {/* Logo */}
            <IconButton Svg={SVGs.gastonOuvertureLogo} />

            {/* Card */}
            <div className="flex flex-col bg-white gap-8 p-8 border border-grey-300 rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.1)] w-full max-w-[460px]">
                {/* Header */}
                <div className="flex flex-col gap-3">
                    <h2 className="text-3xl text-grey-900 font-nunito font-bold">
                        Connexion
                    </h2>
                    <p className="text-base text-grey-500 font-nunito font-normal">
                        Connectez-vous à votre espace ouvreur
                    </p>
                </div>

                {/* Form */}
                <form className="flex flex-col gap-5 w-full" onSubmit={formik.handleSubmit}>
                    {/* Email field */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-semibold text-grey-700">
                            Adresse e-mail
                        </label>
                        <div className={`flex flex-row rounded-lg border-2 p-3.5 bg-white items-center transition-colors duration-200 ${
                            hasEmailError
                                ? 'border-orange-500'
                                : 'border-grey-300 focus-within:border-backoffice-500'
                        }`}>
                            <input
                                type="text"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="nom@exemple.com"
                                className="border-0 outline-none w-full text-base text-grey-900 placeholder:text-grey-400"
                                onFocus={() => formik.setFieldError('email', '')}
                            />
                            {hasEmailError &&
                                <IconButton text='' Svg={SVGs.warningIcon} />
                            }
                        </div>
                        {hasEmailError && (
                            <div className="text-sm text-orange-600">{formik.errors.email}</div>
                        )}
                    </div>

                    {/* Password field */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-semibold text-grey-700">
                            Mot de passe
                        </label>
                        <div className={`flex flex-row rounded-lg border-2 p-3.5 bg-white items-center transition-colors duration-200 ${
                            hasPasswordError
                                ? 'border-orange-500'
                                : 'border-grey-300 focus-within:border-backoffice-500'
                        }`}>
                            <input
                                type={passwordConfirmationShown ? "text" : "password"}
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Saisissez votre mot de passe"
                                className="border-0 outline-none w-full text-base text-grey-900 placeholder:text-grey-400"
                                onFocus={() => formik.setFieldError('password', '')}
                            />
                            {hasPasswordError &&
                                <IconButton text='' Svg={SVGs.warningIcon} />
                            }
                            <CustomButtonBO
                                text=''
                                Svg={passwordConfirmationShown ? SVGs.openEye : SVGs.closedEye}
                                onClickProp={togglePasswordConfirmationVisibility}
                                customStyle={'bg-white'}
                            />
                        </div>
                        {hasPasswordError && (
                            <div className="text-sm text-orange-600">{formik.errors.password}</div>
                        )}
                    </div>

                    {/* Forgot password link */}
                    <div
                        onClick={() => navigate(`/password-reset`)}
                        className="text-sm text-backoffice-700 hover:text-backoffice-900 font-semibold cursor-pointer self-end transition-colors duration-150"
                    >
                        Mot de passe oublié ?
                    </div>

                    {/* Error message */}
                    {errorMsg && (
                        <div className="flex items-center gap-2 p-3 bg-red-25 border border-red-300 rounded-lg">
                            <p className="text-sm text-red-700 font-medium">{errorMsg}</p>
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        className={`w-full py-3.5 rounded-lg text-white text-base font-bold border-none transition-all duration-200 flex items-center justify-center gap-2 ${
                            canSubmit
                                ? 'bg-backoffice-700 cursor-pointer hover:bg-backoffice-800 active:scale-[0.98] shadow-sm hover:shadow-md'
                                : 'bg-grey-300 text-grey-500 cursor-not-allowed'
                        }`}
                        type="submit"
                        disabled={!canSubmit}
                    >
                        {formik.isSubmitting ? (
                            <>
                                <TailSpin color="#ffffff" height={20} width={20} />
                                Connexion en cours...
                            </>
                        ) : (
                            'Se connecter'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default LoginScreenRouteSetter;
